import { IRootStore } from 'shared/entities/store/rootStore';
import {
  AppNotificationType,
  BannerPriority
} from 'shared/entities/appNotifications';

import { PollingModel, TimeoutHandlerType } from './PollingModel';

const BANNER_ID = 'POP_UP_WINDOW_BANNER';

export class PollingWindowModel extends PollingModel {
  private openedWindow: Window | null = null;
  private rootStore: IRootStore;

  constructor({
    rootStore,
    cb,
    showTimeoutNotification = true
  }: {
    rootStore: IRootStore;
    cb: TimeoutHandlerType;
    showTimeoutNotification?: boolean;
  }) {
    super({
      cb,
      cbOnMaxAttempts: () => {
        if (showTimeoutNotification) {
          rootStore.appNotificationsStore.open({
            title: (t) =>
              t('PollingModel.notifications.timeExceeded', {
                ns: 'models'
              }),
            type: AppNotificationType.error
          });
        }

        return {
          isError: false
        };
      }
    });
    this.rootStore = rootStore;
  }

  private closeWindow = () => {
    if (!this.openedWindow) {
      return;
    }
    try {
      this.openedWindow.close();
    } catch {
      // COOP может разорвать связь с cross-origin окном: локальную ссылку всё равно очищаем.
    }
    this.openedWindow = null;
  };

  private _openWindow = (windowUrl: string): void => {
    this.openedWindow = window.open(windowUrl);

    if (!this.openedWindow) {
      this.rootStore.appNotificationsStore.openBanner({
        id: BANNER_ID,
        type: AppNotificationType.warning,
        title: (t) =>
          t('PollingModel.notifications.windows', {
            ns: 'models'
          }),
        priority: BannerPriority.extra
      });
    }
  };

  closePollingBanner = (): void => {
    this.rootStore.appNotificationsStore.closeBanner(BANNER_ID);
  };

  get hasOpenedWindow(): boolean {
    if (!this.openedWindow || this.openedWindow.closed) {
      this.openedWindow = null;

      return false;
    }

    return true;
  }

  get openedWindowReference(): Window | null {
    return this.openedWindow;
  }

  stopPolling = (closeWindow = true): void => {
    if (closeWindow) {
      this.closeWindow();
    }
    super.stopPolling();
  };

  /** Открывает пустое окно синхронно, пока ещё действует жест пользователя; адрес задаётся позже через setWindowUrl */
  openEmptyWindow = (): boolean => {
    this._openWindow('about:blank');

    return this.hasOpenedWindow;
  };

  setWindowUrl = (url: string): boolean => {
    if (!this.hasOpenedWindow || !this.openedWindow) {
      return false;
    }
    try {
      this.openedWindow.location.href = url;
      return true;
    } catch {
      this.openedWindow = null;
      return false;
    }
  };

  closeOpenedWindow = (): void => {
    this.closeWindow();
  };

  openWindow(windowUrl: string): Promise<BaseResponse> {
    this._openWindow(windowUrl);

    return super.startPolling();
  }
}
